// /frontend/src/components/layout/AdminLayout.jsx
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import { 
  FaHome, 
  FaShoppingBag, 
  FaUsers, 
  FaBoxes, 
  FaFileInvoiceDollar, 
  FaCog, 
  FaSignOutAlt,
  FaBars,
  FaTimes,
  FaDraftingCompass // Added icon for design requests
} from 'react-icons/fa';

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const isAdmin = isAuthenticated && user?.role === 'admin';
  
  // Only show full dashboard if user is admin and not on login page
  const showFullDashboard = isAdmin && !location.pathname.includes('/admin/login');
  
  const handleLogout = () => {
    dispatch(logout());
    navigate('/admin/login');
  };
  
  // Navigation items - Added Design Requests
  const navItems = [
    { path: '/admin/dashboard', icon: <FaHome className="mr-3" />, label: 'Dashboard' },
    { path: '/admin/products', icon: <FaShoppingBag className="mr-3" />, label: 'Products' },
    { path: '/admin/users', icon: <FaUsers className="mr-3" />, label: 'Users' },
    { path: '/admin/orders', icon: <FaBoxes className="mr-3" />, label: 'Orders' },
    { path: '/admin/design-requests', icon: <FaDraftingCompass className="mr-3" />, label: 'Design Requests' },
    { path: '/admin/sales', icon: <FaFileInvoiceDollar className="mr-3" />, label: 'Sales Reports' },
    { path: '/admin/settings', icon: <FaCog className="mr-3" />, label: 'Settings' },
  ];
  
  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar - conditionally shown */}
      {showFullDashboard && (
        <aside className={`z-30 bg-teal-800 text-white w-64 py-4 px-6 fixed inset-y-0 left-0 transform transition duration-200 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}>
          {/* Close button - mobile only */}
          <button 
            onClick={() => setSidebarOpen(false)}
            className="absolute top-4 right-4 md:hidden"
          >
            <FaTimes className="h-6 w-6" />
          </button>
          
          {/* Logo/Header */}
          <div className="flex items-center justify-between mb-10 mt-4">
            <div className="text-xl font-bold">HALE Admin</div>
          </div>
          
          {/* Navigation */}
          <nav className="space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center py-3 px-4 transition-colors rounded-md ${
                  location.pathname === item.path || 
                  (item.path.includes('/admin/design-requests') && location.pathname.includes('/admin/design-requests'))
                    ? 'bg-teal-700 text-white'
                    : 'text-teal-100 hover:bg-teal-700'
                }`}
              >
                {item.icon}
                {item.label}
              </Link>
            ))}
            
            {/* Logout */}
            <button
              onClick={handleLogout}
              className="flex items-center py-3 px-4 text-teal-100 hover:bg-teal-700 transition-colors rounded-md w-full text-left"
            >
              <FaSignOutAlt className="mr-3" />
              Logout
            </button>
          </nav>
        </aside>
      )}
      
      {/* Main content */}
      <div className={`flex-1 ${showFullDashboard ? 'md:ml-64' : ''} flex flex-col min-h-screen`}>
        {/* Top navbar */}
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16 items-center">
              {/* Mobile menu button - only show if admin */}
              {showFullDashboard && (
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="md:hidden text-gray-500 focus:outline-none focus:text-gray-700"
                >
                  <FaBars className="h-6 w-6" />
                </button>
              )}
              
              {/* Title */}
              <h1 className="text-xl font-bold text-gray-900">
                {!showFullDashboard ? 'Admin Portal' : getPageTitle(location.pathname)}
              </h1>
              
              {/* Admin Info - only if logged in */}
              {isAdmin && (
                <div className="flex items-center">
                  <div className="mr-3 text-right hidden sm:block">
                    <div className="text-sm font-medium text-gray-900">{user.firstName} {user.lastName}</div>
                    <div className="text-xs text-gray-500">Administrator</div>
                  </div>
                  <div className="h-8 w-8 rounded-full bg-teal-600 text-white flex items-center justify-center">
                    {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>
        
        {/* Page content */}
        <main className="flex-1">
          <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
        
        {/* Footer */}
        <footer className="bg-white py-4 border-t">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-sm text-center text-gray-500">
              &copy; {new Date().getFullYear()} HALE by HENDERSON. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

// Helper function to get page title based on path - Updated with Design Requests
const getPageTitle = (path) => {
  if (path.includes('/admin/dashboard')) return 'Dashboard';
  if (path.includes('/admin/products')) return 'Product Management';
  if (path.includes('/admin/users')) return 'User Management';
  if (path.includes('/admin/orders')) return 'Order Management';
  if (path.includes('/admin/design-requests')) return 'Design Request Management';
  if (path.includes('/admin/sales')) return 'Sales Reports';
  if (path.includes('/admin/settings')) return 'Settings';
  return 'Admin Portal';
};

export default AdminLayout;